/**
 * Bilder for kameraposisjoner – lastes FØRST ved klikk (thumbnail), fullt bilde kun på forespørsel.
 *
 * URL-er: eksplisitt `thumbnail_url`/`image_url` i feature-attributtene, ellers Worker-konvensjonen
 *   GET <api>/api/projects/<prosjekt>/images/<filnavn>?variant=thumb   (thumbnail)
 *   GET <api>/api/projects/<prosjekt>/images/<filnavn>                 (fullt bilde)
 * Bildene hentes med fetch → blob → objectURL, slik at Worker senere kan kreve innlogging (cookies/
 * Authorization) uten endring her. R2 gjøres aldri offentlig.
 */

import { trimBase } from '../config';

export interface ImageRefs {
  thumb: string | null;
  full: string | null;
  file: string | null;
}

export function imageRefs(props: Record<string, unknown>, apiBase: string, projectId: string, template: string): ImageRefs {
  const abs = (u: unknown) => (typeof u === 'string' && u ? (/^https?:\/\//i.test(u) ? u : trimBase(apiBase) + (u.startsWith('/') ? u : '/' + u)) : null);
  const file = typeof props.file === 'string' && props.file ? props.file.split(/[\\/]/).pop()! : null;
  const id=typeof props.image_id==='string'?props.image_id:typeof props.id==='string'?props.id:file;
  const byTemplate = (variant: string) => id
    ? template.replace('{api}', trimBase(apiBase)).replace('{project}', encodeURIComponent(projectId)).replace('{image}', encodeURIComponent(id)) + variant
    : null;
  return {
    thumb: abs(props.thumbnail_url) ?? byTemplate('?variant=thumb'),
    full: abs(props.image_url) ?? byTemplate(''),
    file,
  };
}

export class ImageLoader {
  private cache = new Map<string, Promise<string | null>>();
  private lifecycle=new AbortController();
  stats={requests:0,bytes:0};

  constructor(private init: RequestInit = {}, private fetchImpl: typeof fetch = (...a) => fetch(...a)) {}

  /** objectURL for bildet, eller null hvis det ikke finnes (404) – markøren fungerer uansett. */
  load(url: string): Promise<string | null> {
    if(this.lifecycle.signal.aborted)return Promise.reject(new DOMException('avsluttet','AbortError'));
    let p = this.cache.get(url);
    if (!p) {
      p = (async () => {
        this.stats.requests++;
        const res = await this.fetchImpl(url, {...this.init,signal:AbortSignal.any([this.lifecycle.signal,AbortSignal.timeout(20000)])});
        if (res.status === 404 || res.status === 410) return null;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        this.lifecycle.signal.throwIfAborted();
        this.stats.bytes+=blob.size;
        if (!blob.type.startsWith('image/') && blob.type !== 'application/octet-stream') throw new Error(`ikke et bilde (${blob.type})`);
        return URL.createObjectURL(blob);
      })();
      p.catch(() => this.cache.delete(url)); // nytt forsøk ved neste klikk
      this.cache.set(url, p);
      while(this.cache.size>32){const [key,old]=this.cache.entries().next().value!;this.cache.delete(key);old.then(u=>u&&URL.revokeObjectURL(u)).catch(()=>{});}
    }
    return p;
  }

  dispose(): void {
    this.lifecycle.abort();
    for (const p of this.cache.values()) p.then((u) => u && URL.revokeObjectURL(u)).catch(() => {});
    this.cache.clear();
  }
}
